import type { MouseEvent, ReactNode } from 'react';
import { Box, Link, Paragraph, PlainLink, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import { ConnectionStatus, ConnectionStatusText } from '@/features/integrations/catalog/enums';
import { IntegrationIcon } from '@/features/integrations/directory/components/integration-icon';
import type { IntegrationConnection } from '@/features/integrations/connections/view-model/types';
import { isEmptyString } from '@/utils/string';
import { getStatusBorderClass } from './utils';
import * as styles from './styles.css';

/** Arbitrary `data-*` attributes forwarded onto the rendered icon element. */
type DataAttributes = Record<`data-${string}`, unknown>;

function isConnectionDisabled(connection: Pick<IntegrationConnection, 'status'>): boolean {
  return (
    connection.status != null &&
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    ([ConnectionStatus.disabled, ConnectionStatus.disconnected] as ConnectionStatus[]).includes(connection.status)
  );
}

function isErrorStatus(connection: Pick<IntegrationConnection, 'status'>): boolean {
  if (connection.status == null) {
    return false;
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return ([ConnectionStatus.authenticationError, ConnectionStatus.configurationError] as ConnectionStatus[]).includes(
    connection.status,
  );
}

function errorStatusText(connection: Pick<IntegrationConnection, 'status'>): string {
  if (connection.status == null) {
    return 'Error';
  }
  return ConnectionStatusText[connection.status];
}

type ConnectionIconConnection = {
  icon?: string;
  imageSrc?: string;
  title?: string;
} & Pick<IntegrationConnection, 'status'>;

type ConnectionIconProps = {
  connection: ConnectionIconConnection;
} & DataAttributes;

export function ConnectionIcon({ connection, ...dataAttributes }: ConnectionIconProps) {
  const icon: ReactNode = <IntegrationIcon integration={connection} size="sm" />;
  const usesBrandLogo = isEmptyString(connection.icon) && !isEmptyString(connection.imageSrc);

  return (
    <Box
      className={`${styles.iconWrapper} ${usesBrandLogo ? styles.brandLogoWrapper : ''} ${getStatusBorderClass(connection)}`}
      data-test-connection-icon
      {...dataAttributes}
    >
      {isConnectionDisabled(connection) ? <Box className={styles.disabled}>{icon}</Box> : icon}
    </Box>
  );
}

type ConnectedItemProps = {
  connection: IntegrationConnection;
  flowTypeLabel: string;
  onTooltipLinkClick?: (connection: IntegrationConnection, event: MouseEvent) => void;
} & DataAttributes;

export function ConnectedItem({
  connection,
  flowTypeLabel,
  onTooltipLinkClick,
  ...dataAttributes
}: ConnectedItemProps) {
  const handleLinkClick = (event: MouseEvent): void => {
    onTooltipLinkClick?.(connection, event);
  };

  const disabled = isConnectionDisabled(connection);
  const errorStatus = isErrorStatus(connection);

  return (
    <Tooltip placement="top" isCustom>
      {(context) => (
        <>
          <PlainLink
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps({ onClick: handleLinkClick })}
            href={connection.href}
          >
            <ConnectionIcon connection={connection} {...dataAttributes} />
          </PlainLink>
          <TooltipContent>
            {flowTypeLabel}:{' '}
            <Link isUnderlined variant="secondary" cursor="pointer" href={connection.href} onClick={handleLinkClick}>
              {connection.name}
            </Link>
            {errorStatus ? (
              <Box mt="025" color="inherit">
                ({errorStatusText(connection)})
              </Box>
            ) : disabled ? (
              <Paragraph size="xs" mt="025" color="inherit">
                This connection is disabled.
              </Paragraph>
            ) : null}
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}
